import { Component, OnInit, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { APP_CONFIGS, APP_ICONS } from 'app/hub-components/constants/app.constants';
import { Account } from 'app/hub-components/models/account.model';
import { AppInfo } from 'app/hub-components/models/app-info.model';
import { User } from 'app/hub-components/models/user.model';
import { SessionService } from 'app/hub-components/services/session/session.service';
import { TrackingService } from 'app/hub-components/services/tracking/tracking.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.less'],
  standalone: false
})
export class MenuComponent implements OnInit, AfterViewInit {
  public items: MenuItem[] = [];
  public visibleItemsCount: number = 0;
  public sidebarVisible: boolean = false;
  public isPublic: boolean = false;
  public user: User;
  public userInitials = '';
  public currentApp: AppInfo;
  public appUrls: { [key: string]: string } = {};
  public currentUrl: string;
  public isBurtAdmin: boolean = false;
  public account: Account;
  public appIcons = APP_ICONS;
  public appConfigs = APP_CONFIGS;
  public showConfigDialog = false;
  public forecastingAppSelected = false;

  @ViewChild('itemsContainer', { static: false }) itemsContainer: ElementRef;

  private apps: AppInfo[] = [];
  private availableApps: Set<string> = new Set();
  private readonly ITEM_HEIGHT = 38.5;
  private readonly ITEM_GAP = 10.5;

  constructor(
    private sessionService: SessionService,
    private trackingService: TrackingService,
  ) { }

  public ngOnInit() {
    this.apps = this.sessionService.apps || [];
    this.user = this.sessionService.user;
    this.account = this.sessionService.account;
    this.isBurtAdmin = this.user?.role === 'burt_admin';
    this.currentApp = this.sessionService.currentApp;
    this.isPublic = this.sessionService.isPublic;

    const userNameSplit = this.user?.name?.trim().split(/\s+/) || [];
    this.userInitials = userNameSplit.map(part => part.charAt(0).toUpperCase()).join('');

    this.initAvailableApps();
    this.initAppUrls();
    this.initMenuItems();
    this.initCurrentAppName();
  }

  public ngAfterViewInit() {
    setTimeout(() => this.calculateVisibleItems(), 0);
  }

  @HostListener('window:resize')
  public onResize() {
    this.calculateVisibleItems();
  }

  public get visibleItems(): MenuItem[] {
    return this.items.slice(0, this.visibleItemsCount);
  }

  public get hasMoreItems(): boolean {
    return this.items.length > this.visibleItemsCount;
  }

  public openSidebar(event: MouseEvent): void {
    if (event?.target && (event?.target as Element).closest('a')) {
      return;
    }

    event?.stopPropagation();

    this.sidebarVisible = true;
    this.currentUrl = window.location.href.split(':')[1];
  }

  public getAppUrl(app: AppInfo): string {
    if (app.app === 'forecasting' && app.options?.configurations?.[0]?.url) {
      return app.options.configurations[0].url;
    }
    return app.appUrl;
  }

  public getForecastingApp(): AppInfo | undefined {
    return this.apps.find(app => app.app === 'forecasting');
  }

  public getForecastingConfigurations(): Array<{ url: string; entityName: string }> {
    const forecastingApp = this.getForecastingApp();
    return forecastingApp?.options?.configurations?.map(config => ({
      url: config.url,
      entityName: config.entityName || 'Default',
    })) || [];
  }

  public onStatusButtonClick(): void {
    this.trackingService.track('status button clicked');
  }

  private initCurrentAppName(): void {
    this.currentApp.title = this.items.find(item => item.appKey === this.currentApp?.app)?.label || this.currentApp?.title;
  }

  private initAvailableApps(): void {
    this.availableApps = new Set(this.apps.map(app => app.app));
  }

  private initAppUrls(): void {
    this.appUrls = this.apps.reduce((urls, app) => {
      urls[app.app] = this.getAppUrl(app);
      return urls;
    }, {});
  }

  private initMenuItems(): void {
    this.items = APP_CONFIGS
      .filter(config => this.availableApps.has(config.appKey))
      .map(config => {
        const app = this.apps.find(a => a.app === config.appKey);
        const isForecastingWithMultipleConfigs = app?.app === 'forecasting' && app.options?.configurations?.length > 1;
        return {
          appKey: config.appKey,
          label: config.label,
          icon: config.icon,
          expanded: true,
          items: config.items?.map(item => ({
            label: item.label,
            url: `${this.appUrls[config.appKey]}${item.url}`,
          })),
          url: isForecastingWithMultipleConfigs ? '#' : this.getAppUrl(app),
          command: isForecastingWithMultipleConfigs ? () => {
            this.forecastingAppSelected = true;
            this.showConfigDialog = true;
          } : undefined,
        };
      });
  }

  private getSolidQueueUrl(): string {
    return `${this.appUrls['burthub'].replace('//', '//home.')}/solid_queue`;
  }

  private calculateVisibleItems(): void {
    if (!this.items.length) {
      this.visibleItemsCount = 0;
      return;
    }

    if (!this.itemsContainer?.nativeElement) {
      this.visibleItemsCount = 0;
      return;
    }

    const containerHeight = this.itemsContainer.nativeElement.offsetHeight;

    if (containerHeight <= 0) {
      this.visibleItemsCount = 0;
      return;
    }

    const itemsThatFit = Math.floor(containerHeight / (this.ITEM_HEIGHT + this.ITEM_GAP));

    if (itemsThatFit >= this.items.length) {
      this.visibleItemsCount = this.items.length;
    } else {
      this.visibleItemsCount = itemsThatFit - 1;
    }
  }
}
